import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle,
  MessageSquare,
  HelpCircle,
  AlertTriangle,
  Bug,
  Lightbulb,
  User,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

const TICKET_CATEGORIES = [
  { value: 'general', label: 'General Question', icon: HelpCircle, description: 'Questions about using Varyynt' },
  { value: 'account', label: 'Account Issue', icon: User, description: 'Login, profile, or settings problems' },
  { value: 'matching', label: 'Job Matching', icon: AlertTriangle, description: 'Questions about your matches or V-FIT scores' },
  { value: 'bug', label: 'Report a Bug', icon: Bug, description: 'Something isn\'t working correctly' },
  { value: 'feedback', label: 'Feature Request', icon: Lightbulb, description: 'Suggestions for improvements' },
];

export default function SupportTicketPage() {
  const { user, authAxios } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  
  const [formData, setFormData] = useState({
    category: '',
    subject: '',
    message: '',
    // Auto-populated from user
    name: user?.name || user?.email?.split('@')[0] || '',
    email: user?.email || ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    if (!formData.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Please describe your issue');
      return;
    }

    setLoading(true);
    try {
      const response = await authAxios.post('/candidates/support/tickets', {
        category: formData.category,
        subject: formData.subject,
        message: formData.message
      });
      
      setTicketNumber(response.data.ticket_number);
      setSubmitted(true);
      toast.success('Support ticket submitted!');
    } catch (error) {
      console.error('Failed to submit ticket:', error);
      toast.error('Failed to submit ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg border-slate-100 animate-slideUp">
          <CardHeader className="text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-lime-100 flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-lime-600" />
            </div>
            <CardTitle className="text-2xl font-heading">Ticket Submitted</CardTitle>
            <CardDescription className="text-base">
              Your support request has been received
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-sm text-slate-500">Ticket Number</p>
              <p className="text-2xl font-mono font-bold text-slate-900">{ticketNumber}</p>
            </div>
            <p className="text-sm text-slate-500 text-center">
              We'll respond to <span className="font-medium text-slate-700">{formData.email}</span> within 24-48 business hours.
            </p>
            <div className="flex flex-col gap-3 pt-4">
              <Button onClick={() => navigate('/dashboard')} className="bg-evergreen-950 hover:bg-evergreen-900">
                Return to Dashboard
              </Button>
              <Button variant="outline" onClick={() => navigate('/help')}>
                Browse Help Center
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <Link to="/">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/help')}>
              <HelpCircle className="h-4 w-4 mr-2" />
              Help Center
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-lime-100 mb-4">
            <MessageSquare className="h-7 w-7 text-lime-600" />
          </div>
          <h1 className="text-2xl font-heading text-slate-900 mb-2">Contact Support</h1>
          <p className="text-slate-500">
            Describe your issue and our team will get back to you soon
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Auto-populated User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-500">Your Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="name"
                      value={formData.name}
                      disabled
                      className="pl-10 bg-slate-50 text-slate-600"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-500">Your Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      value={formData.email}
                      disabled
                      className="pl-10 bg-slate-50 text-slate-600"
                    />
                  </div>
                </div>
              </div>

              {/* Category Selection */}
              <div className="space-y-2">
                <Label htmlFor="category">Category <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_CATEGORIES.map((cat) => {
                      const Icon = cat.icon;
                      return (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-slate-500" />
                            <span>{cat.label}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {formData.category && (
                  <p className="text-xs text-slate-500">
                    {TICKET_CATEGORIES.find(c => c.value === formData.category)?.description}
                  </p>
                )}
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject">Subject <span className="text-red-500">*</span></Label>
                <Input
                  id="subject"
                  placeholder="Brief summary of your issue"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  maxLength={100}
                  data-testid="subject-input"
                />
                <p className="text-xs text-slate-400 text-right">{formData.subject.length}/100</p>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">Description <span className="text-red-500">*</span></Label>
                <Textarea
                  id="message"
                  placeholder="Please describe your issue in detail. Include any relevant information like what you were trying to do, what happened, and any error messages you saw."
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  rows={6}
                  className="resize-none"
                  data-testid="message-textarea"
                />
                <p className="text-xs text-slate-400">
                  The more details you provide, the faster we can help
                </p>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-evergreen-950 hover:bg-evergreen-900"
                  data-testid="submit-ticket-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Ticket
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Help Tip */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Looking for quick answers? Check out our{' '}
          <Link to="/help" className="text-lime-600 hover:text-lime-700 font-medium">
            Help Center
          </Link>
        </p>
      </main>
    </div>
  );
}
